import styled from '@emotion/styled'
import {
  DragAndDropCard,
  AddButton,
  PurchaseReceiptCard,
  RunOcr,
  BatchOCR
} from './index'
import UiSelect from '@components/Select'
import RequiredField from '@components/RequiredField'
import { useState, useEffect, useCallback } from 'react'
import Grid from '@mui/material/Grid'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { getFormatedDateForCRM } from '@utils/helpers'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import ClearIcon from '@mui/icons-material/Clear'
import {
  addOtherFileWithData,
  addOtherPurchaseFile,
  addPOSReceiptFileWithData,
  addPurchasePaymentFileWithData,
  addPurchaseStampCard,
  addPurchaseStampFileWithData,
  removePOSReceiptFile,
  removePurchaseOtherFilterFiles,
  removePurchaseOthers,
  removePurchasePayment,
  removePurchasePaymentFile,
  removePurchaseRequest,
  removePurchaseStampCard,
  removePurchaseStampFile,
  updateDropDown,
  setPurchaseReceiptAttributes,
  purchaseReceiptVerifyAll,
  addPR,
  setPurchasePaymentAttributes,
  setCCSlip,
  addCCSlip
} from '@slices/purchaseReceiptSlice'
import { setApiCall, removeApiCall } from '@slices/apiCallCheckSlice'
import { setFilterFiles, removeUploadedDoc } from '@slices/customerFiles'
import { generateUUID } from '@utils/helpers'
import { useCustomer } from 'api'
import { store } from '@app/store'
import moment from 'moment'
import { api } from 'lib/axios'
import { getUserId } from 'lib/auth/tokenStorage'
import { useLogActivity } from 'api'

const extractFileId = file => file?.file_obj?.id || file?.id || ''

// Utility function to process purchase request files with proper structure
const processPurchaseRequestFiles = (pr, additionalFile = null) => {
  const files_arr = {}

  // Process POS receipt files
  if (pr?.files?.pos_receipt && Array.isArray(pr.files.pos_receipt)) {
    pr.files.pos_receipt.forEach((file, idx) => {
      const fileId = extractFileId(file)
      if (fileId) {
        files_arr[idx] = { pos_receipt_image: fileId }
      }
    })
  }

  let counter = Object.keys(files_arr).length

  // Process SAT invoice files
  if (pr?.files?.sat_invoice && Array.isArray(pr.files.sat_invoice)) {
    pr.files.sat_invoice.forEach(file => {
      const fileId = extractFileId(file)
      if (fileId) {
        files_arr[counter++] = { sat_invoice_image: fileId }
      }
    })
  }

  // Process POS stamp files (multiple cards, flattened as array of objects)
  if (pr?.files?.purchase_stamp && Array.isArray(pr.files.purchase_stamp)) {
    pr.files.purchase_stamp.forEach(group => {
      if (Array.isArray(group)) {
        group.forEach(file => {
          const fileId = extractFileId(file)
          if (fileId) {
            files_arr[counter++] = { purchase_stamp: fileId }
          }
        })
      }
    })
  }

  // Process purchase others files
  if (pr?.purchase_others && Array.isArray(pr.purchase_others)) {
    pr.purchase_others.forEach(group => {
      if (Array.isArray(group)) {
        group.forEach(file => {
          const fileId = extractFileId(file)
          if (fileId) {
            files_arr[counter++] = { purchase_others: fileId }
          }
        })
      }
    })
  }

  // Process additional file if provided
  if (additionalFile) {
    const fileId = extractFileId(additionalFile)
    if (fileId) {
      files_arr[counter] = { pos_receipt_image: fileId }
    }
  }

  return files_arr
}

const PurchaseReceiptDocuments = () => {
  const dispatch = useDispatch()
  const { id: customerId } = useParams()

  // State for managing dropdown data for each purchase request
  const [selectedAffiliatesData, setSelectedAffiliatesData] = useState([])
  const [storeLocator, setStoreLocator] = useState([])
  const [loadingStates, setLoadingStates] = useState({
    addPurchaseRequest: false,
    addOtherCard: false,
    documentDeletewithfile: false,
    deletingOtherCards: {}
  })

  // Get affiliate data from Redux
  const { affiliatesData } = useSelector(state => state.purchaseReceipt)
  const customer = useSelector(state => state.customer)
  const files = useSelector(state => state.customer_files.files)
  const customer_files = useSelector(state => state?.customer_files)
  const affiliate_location = useSelector(
    state => state.affiliate_company_location
  )
  const user = useSelector(state => state.user)
  const passport = useSelector(state => state.passport)
  const otherID = useSelector(state => state.otherID)
  const credit_cards = useSelector(state => state?.credit_cards?.cards)
  const purchase_cards = useSelector(state => state?.paymentCards?.paymentCards)
  const tripAir = useSelector(state => state.tripAir)
  const tripSea = useSelector(state => state.tripSea)
  const purchase_requests = useSelector(state => state.purchaseReceipt.pr)
  const show_linked_passport = useSelector(
    state => state.purchaseReceipt.linked_passport
  )
  const pdf_Data = useSelector(state => state.pdf_data)
  const purchase_total = useSelector(state => state.purchaseTotal)
  const idleTime = useSelector(state => state.idleTime)
  const apiCall = useSelector(state => state.apiCall)
  const securelink = useSelector(state => state.securelink)
  const arnFiles = useSelector(state => state.arn_files.arn)
  const [posVerified, setPosVerified] = useState(false)
  const [allPosVerified, setAllPosVerified] = useState(false)
  const [paymentVerified, setPaymentVerified] = useState(false)

  const {
    affiliates = [],
    regions = {},
    locations = {},
    stores = {}
  } = affiliatesData

  const agent_id = getUserId()

  const { logApiActivity } = useLogActivity()

  const {
    handleAddNewPurchaseRequest,
    handleSavePurchaseReceiptData,
    handleSetIdleTime,
    handleSetPortalTimeLog,
    handleSavePurchaseRequestData,
    handleDeletePurchaseRequest,
    handleAddNewPurchasePayment,
    handleDeletePurchasePayment,
    handleDeletePOSItem,
    handleDeleteCCSlip,
    handleDeleteFactura,
    handleDeleteOther,
    handleDeletePOSStamp
  } = useCustomer()

  const setLoadingState = (buttonType, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [buttonType]: isLoading
    }))
  }

  // Helper function to manage per-card deletion loading with scoped keys
  const setOtherCardDeleting = (type, prIndex, otherIndex, isDeleting) => {
    const key = `${type}-${prIndex}-${otherIndex}`

    setLoadingStates(prev => ({
      ...prev,
      deletingOtherCards: {
        ...prev.deletingOtherCards,
        [key]: isDeleting
      }
    }))
  }
  useEffect(() => {
    if (!purchase_requests.length) return

    const affiliates = []
    purchase_requests.forEach((item, index) => {
      const attrs = item?.attributes || {}
      const {
        id,
        affiliate_company_id,
        affiliate_region_id,
        affiliate_location_id,
        affiliate_store_id
      } = attrs

      // Use a unique identifier: either the actual ID or a combination of index and timestamp
      const uniqueId = id || `temp_${index}_${Date.now()}`

      affiliates.push({
        id: uniqueId,
        index: index, // Keep track of the index for reference
        affiliate: affiliate_company_id || '',
        region: affiliate_region_id || '',
        location: affiliate_location_id || '',
        store: affiliate_store_id || '',
        storeLocator:
          affiliate_store_id && affiliate_location_id
            ? `${affiliate_store_id}/${affiliate_location_id}`
            : null
      })

      // Set stores with locations for existing data
      if (affiliate_region_id) {
        setStoresWithLocations(uniqueId, affiliate_region_id)
      }
    })

    setSelectedAffiliatesData(affiliates)
  }, [purchase_requests, affiliatesData])

  // Function to set stores with locations (placeholder for API call)
  const setStoresWithLocations = (prId, region) => {
    const allLocations = affiliatesData?.locations[region]
    if (allLocations?.length > 0) {
      let storesArr = []
      allLocations?.forEach(location => {
        const locationStores = affiliatesData?.stores[location.id]
        if (locationStores) {
          locationStores.forEach(store => {
            const obj = {
              value: `${store?.id}/${location?.id}`,
              label: `${location.name} / ${store.name}`,
              location: location.id,
              store: store.id,
              prId
            }
            storesArr.push(obj)
          })
        }
      })
      setStoreLocator(prevState => {
        const existing = prevState.find(item => item.id === prId)
        if (existing) {
          return prevState.map(item =>
            item.id === prId ? { ...item, options: storesArr } : item
          )
        } else {
          return [...prevState, { id: prId, options: storesArr }]
        }
      })
    }
  }

  // Function to track activity (placeholder)
  const trackActivity = async () => {
    try {
      // Check if required variables are available
      if (!agent_id) {
        console.error('agent_id is not available:', agent_id)
        return
      }

      if (!customer?.id) {
        console.error('customer.id is not available:', customer)
        return
      }

      const now = moment()
      const utcMoment = now.utc()
      const formattedTime = utcMoment.format('YYYY-MM-DD HH:mm:ss')

      const body = {
        data: {
          type: 'gb_action_tracking_logs',
          attributes: {
            affiliate_member_id: agent_id,
            action: 'select_affiliate',
            action_time: formattedTime,
            parent_id: customer.id
          }
        }
      }
      const response = await api.post('Api/V8/module', body)
    } catch (error) {
      console.error('Error in trackActivity:', error)
    }
  }

  // Handle dropdown changes with API calls and Redux dispatch
  const handleChangeAffiliate = async (id, field, value) => {
    if (field === 'region') {
      await setStoresWithLocations(id, value)
    }

    setSelectedAffiliatesData(prevData => {
      const updatedData = prevData.map(item =>
        item.id === id
          ? {
            ...item,
            [field]: value,
            ...(field === 'affiliate'
              ? {
                region: '',
                location: '',
                store: '',
                storeLocator: ''
              }
              : field === 'region'
                ? {
                  location: '',
                  store: '',
                  storeLocator: ''
                }
                : field === 'location'
                  ? {
                    storeLocator: value || ''
                  }
                  : {})
          }
          : item
      )

      dispatch(updateDropDown({ data: updatedData }))
      return updatedData
    })

    if (field === 'affiliate') {
      setStoreLocator(prev =>
        prev?.map(item => (item.id === id ? { ...item, options: [] } : item))
      )
    }
    if (field === 'affiliate') {
      await trackActivity()
    }
    if (field === 'region') {
      setStoreLocator(prev =>
        prev?.map(item => (item.id === id ? { ...item, options: [] } : item))
      )
    }
    if (field === 'location') {
      setStoreLocator(prev =>
        prev?.map(item => (item.id === id ? { ...item, options: [] } : item))
      )
    }
  }

  // Generate affiliate options from Redux data
  const affiliateOptions = [
    { value: '', label: 'Select Affiliate' },
    ...affiliates.map(affiliate => ({
      value: affiliate.id,
      label: affiliate.name
    }))
  ]

  const addNewPurchasePayment = useCallback(
    async pr_no => {
      setLoadingState('addPurchasePayment', true)
      const purchase_request = purchase_requests[pr_no]
      const pp_no = purchase_request?.payment_methods?.length || 0

      const result = await handleAddNewPurchasePayment({
        customer_id: customer.id,
        module_name: 'gb_purchase_payments',
        fields_data: {
          purchase_request_id: purchase_request.attributes.id,
          currency: 'mxn',
          payment_type: 'credit'
        }
      })

      if (result && result.customer_id === customerId) {
        dispatch(addCCSlip({ id: result?.record_id, index: pp_no, pr_no }))
        dispatch(
          setPurchasePaymentAttributes({
            pr_no,
            pp_no,
            attributes: { id: result?.record_id }
          })
        )
      } else {
        dispatch(removePurchasePayment({ pr_no, pp_no }))
      }
      setLoadingState('addPurchasePayment', false)
    },
    [purchase_requests, customer, dispatch]
  )

  const paymentMethodsCheckVerified = async () => {
    const allFlags = []

    purchase_requests.forEach(pr => {
      pr.payment_methods.forEach(pp => {
        let verifyObj = { ...pp.attributes.verify }

        const { payment_type, currency } = pp.attributes

        // Remove irrelevant fields based on type and currency
        if (payment_type === 'cash' && currency === 'usd') {
          delete verifyObj.date_of_transaction
          delete verifyObj.last_digits
          delete verifyObj.exist_card_number
        } else if (payment_type === 'cash' && currency === 'mxn') {
          delete verifyObj.date_of_transaction
          delete verifyObj.last_digits
          delete verifyObj.exist_card_number
          delete verifyObj.exchange_rate
        } else if (payment_type === 'financing') {
          delete verifyObj.exist_card_number
          delete verifyObj.exchange_rate
        } else if (
          (payment_type === 'credit' || payment_type === 'debit') &&
          currency === 'mxn'
        ) {
          delete verifyObj.exchange_rate
        }

        // Push remaining values
        Object.values(verifyObj).forEach(v => allFlags.push(v))
      })
    })

    const isAllVerified = allFlags.every(v => v === true)

    if (isAllVerified) {
      setPaymentVerified(true)
      await MainGbFormTimeOutFunc()
    } else {
      setPaymentVerified(false)
    }
  }

  const purchasePaymentVerify = useCallback(
    async (pr_no, pp_no, verifyAll) => {
      // Early return for 'add' case - don't access pp.attributes when adding new payment
      if (verifyAll === 'add') {
        await addNewPurchasePayment(pr_no)
        return
      }

      const pp = purchase_requests[pr_no].payment_methods[pp_no]

      // Add safety check in case pp or pp.attributes is undefined
      if (!pp || !pp.attributes) {
        console.error('Payment method or attributes not found', { pr_no, pp_no })
        return
      }

      const { payment_type, currency, verify, id, verified } = pp.attributes

      let obj = { ...verify }

      // Remove keys based on payment type and currency
      if (payment_type === 'cash') {
        delete obj.date_of_transaction
        delete obj.last_digits
        delete obj.exist_card_number
        if (currency === 'mxn') delete obj.exchange_rate
      } else if (payment_type === 'financing') {
        delete obj.exist_card_number
        delete obj.exchange_rate
      } else if (
        (payment_type === 'credit' || payment_type === 'debit') &&
        currency === 'mxn'
      ) {
        delete obj.exchange_rate
      }

      const values = Object.values(obj)
      const checker = values.every(v => v === true)

      // Set verified status
      dispatch(
        setPurchasePaymentAttributes({
          pr_no,
          pp_no,
          attributes: { verified: checker }
        })
      )

      await paymentMethodsCheckVerified()

      if (verifyAll && verifyAll !== 'add') {
        const api_result = await handleSaveFieldsVerificationData(
          id,
          'gb_payment_method',
          'gb_purchase_payments',
          'verified',
          checker ? 1 : 0,
          verify
        )

        if (api_result === false) {
          dispatch(paymentMethodVerifyAll({ pr_no, pp_no, value: !verified }))
          dispatch(
            setPurchasePaymentAttributes({
              pr_no,
              pp_no,
              attributes: { verified: !verified }
            })
          )
          await paymentMethodsCheckVerified()
        }
      }
    },
    [
      purchase_requests,
      setPurchasePaymentAttributes,
      paymentMethodsCheckVerified,
      addNewPurchasePayment
    ]
  )

  const savePurchaseRequest = useCallback(
    async (i, additionalFile = null) => {
      // Get fresh data from Redux state using store.getState()
      const currentPurchaseRequests = store.getState().purchaseReceipt.pr
      const pr = { ...currentPurchaseRequests[i] }
      const tripId = tripAir?.attributes?.id || tripSea?.id

      let data = Object.assign({}, pr.attributes)
      data.date_of_purchase = getFormatedDateForCRM(data.date_of_purchase)
      data.trip_id = tripId
      const selectedAffiliateId = pr.attributes?.affiliate_company_id
      data.merchant_name =
        affiliates?.find(affiliate => affiliate.id === selectedAffiliateId)
          ?.name ||
        pr.attributes?.merchant_name ||
        ''

      delete data.files
      delete data.ocr
      delete data.verify
      delete data.entered_mxn_total
      delete data.merchant_first_name
      delete data.merchant_last_name

      const purchaseRequestId = pr?.attributes?.id
      if (purchaseRequestId) {
        data.id = purchaseRequestId
      }

      // Process files using the same approach as savePassport
      const files_arr = processPurchaseRequestFiles(pr, additionalFile)

      // Process purchase items
      const purchase_items_data = {}
      if (pr?.items && Array.isArray(pr.items)) {
        pr.items.forEach((item, idx) => {
          if (item) {
            const {
              valid_sat,
              sat_verified,
              invalid_sat,
              invalid_sat_code_number,
              invalid_sat_code_category,
              valid_sat_code_number,
              valid_sat_code_category,
              sat_code_number_message,
              sat_code_category_message,
              invalid_qty,
              valid_qty,
              qty_message,
              invalid_cost,
              valid_cost,
              cost_message,
              ...cleanedItem
            } = item

            purchase_items_data[idx] = {
              ...cleanedItem,
              name: item.description
            }
          }
        })
      }

      // Process purchase payment methods
      const purchase_payment_data = []
      if (pr?.payment_methods && Array.isArray(pr.payment_methods)) {
        pr.payment_methods.forEach((pp, ppIndex) => {
          if (pp && pp.attributes) {
            const paymentAttributes = {
              ...pp.attributes,
              exp_date: getFormatedDateForCRM(pp.attributes.exp_date),
              date_of_transaction: getFormatedDateForCRM(
                pp.attributes.date_of_transaction
              ),
              card_number: pp.attributes.card_number?.replace(/\s/g, ''),
              name: pp.attributes.credit_card_number
            }

            delete paymentAttributes.verify
            delete paymentAttributes.ocr
            delete paymentAttributes.existing_payment_card

            const paymentFiles = {}
            let fileCounter = 0

            if (pp?.files?.card && Array.isArray(pp.files.card)) {
              pp.files.card.forEach(file => {
                const fileId = extractFileId(file)
                if (fileId) {
                  paymentFiles[fileCounter++] = { credit_card: fileId }
                }
              })
            }

            if (pp?.files?.slip && Array.isArray(pp.files.slip)) {
              pp.files.slip.forEach(file => {
                const fileId = extractFileId(file)
                if (fileId) {
                  paymentFiles[fileCounter++] = { credit_card_slip: fileId }
                }
              })
            }
            const paymentMethodObj = {
              0: {
                basic: paymentAttributes
              },
              1: {
                ocr: pp.attributes.ocr || {}
              },
              2: {
                verification: pp.attributes.verify || {}
              },
              3: {
                files: paymentFiles
              }
            }

            purchase_payment_data.push(paymentMethodObj)
          }
        })
      }

      dispatch(setApiCall({ api_type: 'purchase_saving', call: true }))

      // Create the correct payload structure
      const purchaseRequestData = [
        {
          basic: { ...data }
        },
        {
          ocr: pr.attributes.ocr || {}
        },
        {
          verification: pr.attributes.verify || {}
        },
        {
          files: files_arr
        },
        {
          purchase_items: purchase_items_data
        },
        {
          purchase_payments: purchase_payment_data
        }
      ]

      const apiCallData = [purchaseRequestData]

      const payload = {
        apiCallData: apiCallData,
        contact_id: customer.id,
        trip_id: tripId
      }

      await handleSavePurchaseRequestData(payload)

      dispatch(removeApiCall({ api_type: 'purchase_saving' }))
    },
    [
      customer,
      affiliates,
      dispatch,
      handleSavePurchaseRequestData,
      tripAir,
      tripSea
    ]
  )

  const savePR = async idx => {
    await new Promise(resolve => setTimeout(resolve, 100))

    savePurchaseRequest(idx)
  }

  const MainGbFormTimeOutFunc = async () => {
    const passportState = passport.verified
    const purchaseCardState = purchase_cards.some(
      card => card.attributes.verified === true
    )

    const tripAirState = tripAir.attributes.verify.date_of_visit
    const airBoardingState = tripAir.attributes.boarding_verified
    const seaCruiseState = tripSea.cruise_verified

    let verifiedPurchase = true
    let verifiedPayment = true

    purchase_requests.forEach(pr => {
      if (pr.attributes.verified !== true && pr.attributes.verified !== '1') {
        verifiedPurchase = false
      }

      pr.payment_methods.forEach(card => {
        if (
          card.attributes.verified !== true &&
          card.attributes.verified !== '1'
        ) {
          verifiedPayment = false
        }
      })
    })

    const isSeaTrip = tripAir.attributes.air_sea_type === 'sea'

    const allSeaConditions =
      verifiedPurchase &&
      verifiedPayment &&
      passportState === true &&
      purchaseCardState === true &&
      seaCruiseState === true

    const allAirConditions =
      verifiedPurchase &&
      verifiedPayment &&
      passportState === true &&
      purchaseCardState === true &&
      tripAirState &&
      airBoardingState === true

    const shouldProceed =
      (isSeaTrip && allSeaConditions) || (!isSeaTrip && allAirConditions)

    if (shouldProceed && files.length > 0 && passport.flag !== true) {
      await handleSetIdleTime({
        agent_id: agent_id,
        contact_id: customer.id,
        total_idle_time: Math.round(idleTime.idle_time_total),
        section_name: 'total_idle_time'
      })
      await handleSetPortalTimeLog('main_form', {
        previous_section: 'main_form',
        current_section: 'main_form',
        agent_id: agent_id,
        contact_id: customer.id
      })
    }
  }

  const addNewPurchaseRequest = async () => {
    setLoadingState('addPurchaseRequest', true)
    const index = purchase_requests.length
    const customerAffiliate =
      affiliates?.find(
        affiliate => affiliate.id === customer?.affiliate_company_id
      ) || {}
    const { id: affiliateId = '', name: merchantName = '' } = customerAffiliate
    const tripId = tripAir?.attributes?.id || tripSea?.id

    // Set loading state for adding new purchase
    setApiCall({ api_type: 'add_purchase', call: true })

    const agentId = getUserId() || ''
    const purchaseRequestPayload = {
      customer_id: customerId,
      purchase_fields: {
        trip_id: tripId,
        iva: 16,
        currency: 'usd',
        verified: false,
        factura_verified: '0',
        is_factura_overrided: '0',
        is_factura_desputed: '0',
        affiliate_company_id: affiliateId,
        merchant_name: merchantName
      },
      payment_fields: {
        currency: 'mxn',
        payment_type: 'credit'
      }
    }

    try {
      const response = await logApiActivity({
        contactId: customerId,
        agentId,
        apiEndpoint: '/Api/V8/custom/savedata/create-purchase-request',
        actionType: 'add_purchase_request',
        requestPayload: purchaseRequestPayload,
        apiCall: async () => {
          return await handleAddNewPurchaseRequest(purchaseRequestPayload)
        }
      })

      if (
        response &&
        response.purchase_request_id &&
        response.purchase_payment_id
      ) {
        const { purchase_request_id, purchase_payment_id } = response

        dispatch(addPR({ id: purchase_request_id, index }))
        dispatch(
          setPurchaseReceiptAttributes({
            pr_no: index,
            attributes: { id: purchase_request_id }
          })
        )
        dispatch(
          setCCSlip({
            pr_no: index,
            index: 0,
            id: purchase_payment_id
          })
        )
      } else {
        dispatch(removePurchaseRequest({ pr_no: index }))
      }
    } catch (error) {
      console.error('Error adding new purchase request:', error)
      dispatch(removePurchaseRequest({ pr_no: index }))
    } finally {
      // Remove loading state
      removeApiCall({ api_type: 'add_purchase' })
      setLoadingState('addPurchaseRequest', false)
    }
  }

  const savePurchaseReceiptData = async (
    p_id,
    p_name,
    mod_name,
    f_name,
    f_value,
    verification_data,
    products_info,
    customerId
  ) => {
    const response = await handleSavePurchaseReceiptData({
      parent_id: p_id,
      parent_name: p_name,
      module_name: mod_name,
      field_name: f_name,
      field_value: f_value,
      verification: verification_data,
      products_info,
      contact_id: customerId
    })

    return response.success === true
  }

  const purchasesCheckVerified = async () => {
    const allVerified = purchase_requests.every(
      pr => pr.attributes.verified === true || pr.attributes.verified === 1
    )

    setPosVerified(allVerified)
    setAllPosVerified(allVerified)

    if (allVerified) {
      await MainGbFormTimeOutFunc()
    }

    await paymentMethodsCheckVerified()
  }

  const purchaseSlipVerify = async (index, verifyAll, oldCustomer = false) => {
    if (verifyAll === 'add') {
      await addNewPurchaseRequest()
      return
    }
    const request = purchase_requests[index]
    if (!request || !request.attributes) {
      console.error(`Purchase request at index ${index} is undefined or missing attributes`)
      return
    }

    const { attributes, items = [] } = request
    const { currency, verify, id } = attributes

    // Clone and clean the verify object
    const verifyData = { ...verify }
    if (currency === 'mxn') {
      delete verifyData.dollar_total
      delete verifyData.exchange_rate
    }

    const isHeaderVerified = Object.values(verifyData).every(v => v === true)

    // Check if item fields are verified
    const isItemVerified = items.length === 0 || items.every(item => {
      return oldCustomer
        ? item.cost_verified &&
        item.description_verified &&
        item.qty_verified &&
        item.sat_verified
        : item.cost_verified &&
        item.qty_verified &&
        item.sat_code_number_verified &&
        item.sat_code_category_verified
    })

    const allVerified = isHeaderVerified && isItemVerified

    // Update local verified flag
    dispatch(
      setPurchaseReceiptAttributes({
        pr_no: index,
        attributes: { verified: allVerified }
      })
    )

    await purchasesCheckVerified()

    // Handle different verifyAll modes
    if (verifyAll && verifyAll !== 'add' && verifyAll !== 'start') {
      const productsInfo = items.map(item => {
        return oldCustomer
          ? {
            id: item.id,
            cost_verified: item.cost_verified,
            description_verified: item.description_verified,
            qty_verified: item.qty_verified,
            sat_verified: item.sat_verified
          }
          : {
            id: item.id,
            cost_verified: item.cost_verified,
            qty_verified: item.qty_verified,
            sat_code_number_verified: item.sat_code_number_verified,
            sat_code_category_verified: item.sat_code_category_verified
          }
      })

      const apiResult = await savePurchaseReceiptData(
        id,
        'gb_invoices',
        'gb_invoices',
        'verified',
        allVerified ? 1 : 0,
        verify,
        productsInfo
      )

      if (apiResult === false) {
        const newVerifiedState = !attributes.verified
        dispatch(
          purchaseReceiptVerifyAll({ pr_no: index, value: newVerifiedState })
        )
        dispatch(
          setPurchaseReceiptAttributes({
            pr_no: index,
            attributes: { verified: newVerifiedState }
          })
        )
        await purchasesCheckVerified()
      }
    }
  }

  // Function to delete a purchase request
  const deletePurchaseRequest = async index => {
    const purchaseRequest = purchase_requests[index]

    const filesToRestore = []

    if (purchaseRequest.files?.pos_receipt?.length > 0) {
      purchaseRequest.files.pos_receipt.forEach(file => {
        filesToRestore.push(file)
      })
    }

    if (purchaseRequest.payment_methods?.length > 0) {
      purchaseRequest.payment_methods.forEach(pp => {
        if (pp.files?.slip?.length > 0) {
          pp.files.slip.forEach(file => {
            filesToRestore.push(file)
          })
        }
      })
    }

    if (purchaseRequest.files?.sat_invoice?.length > 0) {
      purchaseRequest.files.sat_invoice.forEach(file => {
        filesToRestore.push(file)
      })
    }

    if (purchaseRequest.purchase_others?.length > 0) {
      purchaseRequest.purchase_others.forEach(otherFiles => {
        if (otherFiles?.length > 0) {
          otherFiles.forEach(file => {
            filesToRestore.push(file)
          })
        }
      })
    }

    filesToRestore.forEach(file => {
      dispatch(
        setFilterFiles({
          file: {
            file_no: customer_files?.files?.length || 0,
            file: {},
            id: generateUUID(),
            fileName: file?.file_obj?.name || file?.fileName || '',
            status: 'File uploaded successfully.',
            progress: 100,
            isUploaded: true,
            isError: false,
            file_obj: file?.file_obj || file || {}
          }
        })
      )
    })

    await handleDeletePurchaseRequest(purchaseRequest.attributes.id)

    dispatch(removePurchaseRequest({ pr_no: index }))
    if (index === 0) {
      purchaseSlipVerify(0)
    } else {
      purchaseSlipVerify(index - 1)
    }
  }

  const deletePaymentMethod = useCallback(
    async (pr_no, pp_no, items = []) => {
      try {
        setOtherCardDeleting('payment', pr_no, pp_no, true)

        const paymentMethod = purchase_requests[pr_no]?.payment_methods?.[pp_no]

        const paymentId = paymentMethod?.attributes?.id

        if (paymentId) {
          await handleDeletePurchasePayment(paymentId)
        }

        dispatch(removePurchasePayment({ pr_no, pp_no }))

        const verifyIndex =
          pp_no === 0
            ? purchase_requests.length - 1
            : purchase_requests.length - 1

        const verifyPPNo = pp_no === 0 ? 0 : pp_no - 1

        purchasePaymentVerify(verifyIndex, verifyPPNo)

        if (items?.length > 0) {
          items.forEach(item => {
            dispatch(
              setFilterFiles({
                file: {
                  file_no: customer_files?.files?.length || 0,
                  file: {},
                  id: generateUUID(),
                  fileName: item?.file_obj?.name || '',
                  status: 'File uploaded successfully.',
                  progress: 100,
                  isUploaded: true,
                  isError: false,
                  file_obj: item?.file_obj || item
                }
              })
            )
          })
        }
      } catch (error) {
        console.log(error)
      } finally {
        setOtherCardDeleting('payment', pr_no, pp_no, false)
      }
    },
    [dispatch, purchase_requests, purchasePaymentVerify, customer_files]
  )

  const onDropPOSStamp = async (prIdx, cardIdx, item) => {
    const existingLength =
      purchase_requests[prIdx]?.files?.purchase_stamp?.[cardIdx]?.length || 0

    // Allow only one file per POS Stamp card (similar to CC Slip)
    if (existingLength < 1) {
      dispatch(removeUploadedDoc({ id: item?.file_obj?.id }))
      dispatch(
        addPurchaseStampFileWithData({
          pr_no: prIdx,
          card_no: cardIdx,
          file: { ...item, new_relation_addition: true }
        })
      )
      await savePurchaseRequest(prIdx)
    }
  }

  const DeletePOSStampItem = async (prIdx, cardIdx, fileIdx) => {
    try {
      setOtherCardDeleting('stamp', prIdx, cardIdx, true)

      const file =
        purchase_requests[prIdx]?.files?.purchase_stamp?.[cardIdx]?.[fileIdx]
      if (!file) return

      dispatch(
        removePurchaseStampFile({
          pr_no: prIdx,
          card_no: cardIdx,
          file_no: fileIdx
        })
      )

      await handleDeletePOSStamp(
        purchase_requests[prIdx].attributes.id,
        file?.file_obj?.id || file?.id
      )
    } catch (error) {
      console.log(error)
    } finally {
      setOtherCardDeleting('stamp', prIdx, cardIdx, false)
    }
  }

  const DeletePOSStampCard = async (prIdx, cardIdx, stampFiles = []) => {
    try {
      setOtherCardDeleting('stamp', prIdx, cardIdx, true)

      if (!stampFiles || stampFiles.length === 0) {
        dispatch(
          removePurchaseStampCard({
            pr_no: prIdx,
            card_no: cardIdx
          })
        )
        return
      }

      const deletePromises = stampFiles.map(async file => {
        const id = file?.file_obj?.id || file?.id
        if (!id) return

        dispatch(
          setFilterFiles({
            file: {
              file_no: customer_files?.files?.length || 0,
              file: {},
              id: generateUUID(),
              fileName: file?.file_obj?.name || file?.fileName || '',
              status: 'File uploaded successfully.',
              progress: 100,
              isUploaded: true,
              isError: false,
              file_obj: file?.file_obj || file
            }
          })
        )

        await handleDeletePOSStamp(
          purchase_requests[prIdx].attributes.id,
          id
        )
      })

      await Promise.all(deletePromises)

      dispatch(
        removePurchaseStampCard({
          pr_no: prIdx,
          card_no: cardIdx
        })
      )
    } catch (error) {
      console.log(error)
    } finally {
      setOtherCardDeleting('stamp', prIdx, cardIdx, false)
    }
  }

  const onDropPOS = async (idx, item) => {
    const checkData = purchase_requests[idx]?.files?.pos_receipt?.length < 1

    if (checkData) {
      const agentId = getUserId() || ''
      await logApiActivity({
        contactId: customerId,
        agentId,
        apiEndpoint: '/Api/V8/custom/savedata/save-purchase-request-data',
        actionType: 'drag_drop_pos_receipt',
        requestPayload: {
          contact_id: customerId,
          purchase_request_index: idx,
          file_id: item?.file_obj?.id || item?.id
        },
        apiCall: async () => {
          dispatch(removeUploadedDoc({ id: item?.file_obj?.id }))
          dispatch(
            addPOSReceiptFileWithData({
              file_type: 'pos_receipt',
              pr_no: idx,
              file: { ...item, new_relation_addition: true }
            })
          )
          await savePurchaseRequest(idx)
        }
      })
    }
  }

  const DeletePOSItem = async (idx, item) => {
    let id = ''
    let file = purchase_requests[idx].files['pos_receipt'][item]
    if (file?.file_obj?.id) {
      id = file?.file_obj?.id
    } else {
      id = file?.id
    }
    const purchaseRequestId = purchase_requests[idx].attributes?.id
    await handleDeletePOSItem(purchaseRequestId, id)
    dispatch(
      removePOSReceiptFile({
        file_type: 'pos_receipt',
        pr_no: idx,
        file_no: item
      })
    )
  }

  const onDropCCSlip = async (idx, item, ccSlipCount) => {
    const paymentMethod = purchase_requests[idx]?.payment_methods[ccSlipCount]
    // Only allow if payment method has a valid backend ID
    if (!paymentMethod?.attributes?.id) {
      console.error(
        'Payment method has no valid backend ID. Please create the card first.'
      )
      return
    }

    const checkData =
      purchase_requests[idx]?.payment_methods[ccSlipCount]?.files?.slip
        ?.length < 1

    if (checkData) {
      const agentId = getUserId() || ''
      await logApiActivity({
        contactId: customerId,
        agentId,
        apiEndpoint: '/Api/V8/custom/savedata/save-purchase-request-data',
        actionType: 'drag_drop_cc_slip',
        requestPayload: {
          contact_id: customerId,
          purchase_request_index: idx,
          payment_method_index: ccSlipCount,
          file_id: item?.file_obj?.id || item?.id
        },
        apiCall: async () => {
          dispatch(removeUploadedDoc({ id: item?.file_obj?.id }))
          dispatch(
            addPurchasePaymentFileWithData({
              file_type: 'slip',
              pr_no: idx,
              pp_no: ccSlipCount,
              file: { ...item, new_relation_addition: true }
            })
          )
          await savePurchaseRequest(idx)
        }
      })
    }
  }

  const DeleteCCSlip = async (count, subCount, index, file) => {
    try {
      let file =
        purchase_requests[count].payment_methods[index].files['slip'][subCount]
      let id = file?.file_obj?.id || file?.id
      const paymentMethod = purchase_requests[count].payment_methods[index]
      const purchaseRequestId = paymentMethod?.attributes?.id
      dispatch(
        removePurchasePaymentFile({
          file_type: 'slip',
          pr_no: count,
          pp_no: index,
          file_no: subCount
        })
      )
      await handleDeleteCCSlip(purchaseRequestId, id)
    } catch (error) {
      console.log(error)
    }
  }

  const onDropFactura = async (item, idx) => {
    const checkData = purchase_requests[idx]?.files?.sat_invoice?.length < 1

    if (checkData) {
      const agentId = getUserId() || ''
      await logApiActivity({
        contactId: customerId,
        agentId,
        apiEndpoint: '/Api/V8/custom/savedata/save-purchase-request-data',
        actionType: 'drag_drop_factura',
        requestPayload: {
          contact_id: customerId,
          purchase_request_index: idx,
          file_id: item?.file_obj?.id || item?.id
        },
        apiCall: async () => {
          dispatch(removeUploadedDoc({ id: item?.file_obj?.id }))
          dispatch(
            addPOSReceiptFileWithData({
              file_type: 'sat_invoice',
              pr_no: idx,
              file: { ...item, new_relation_addition: true }
            })
          )
          await savePurchaseRequest(idx)
        }
      })
    }
  }

  const DeleteFactura = async (count, index) => {
    try {
      let id = ''
      let file = purchase_requests[count].files['sat_invoice'][index]
      if (file?.file_obj?.id) {
        id = file?.file_obj?.id
      } else {
        id = file?.id
      }
      const purchaseRequestId = purchase_requests[count].attributes?.id
      dispatch(
        removePOSReceiptFile({
          file_type: 'sat_invoice',
          pr_no: count,
          file_no: index
        })
      )
      await handleDeleteFactura(purchaseRequestId, id)
    } catch (error) {
      console.log(error)
    }
  }

  const onDropOther = async (id, idx, oIdx) => {
    // If purchase_others is empty, create the first slot
    if (!purchase_requests[idx]?.purchase_others?.length) {
      dispatch(addOtherPurchaseFile({ pr_no: idx }))
    }

    const checkData =
      purchase_requests[idx]?.purchase_others?.[oIdx]?.length < 2

    if (checkData) {
      const agentId = getUserId() || ''
      await logApiActivity({
        contactId: customerId,
        agentId,
        apiEndpoint: '/Api/V8/custom/savedata/save-purchase-request-data',
        actionType: 'drag_drop_other',
        requestPayload: {
          contact_id: customerId,
          purchase_request_index: idx,
          other_index: oIdx,
          file_id: id?.file_obj?.id || id?.id
        },
        apiCall: async () => {
          dispatch(removeUploadedDoc({ id: id?.file_obj?.id }))
          dispatch(
            addOtherFileWithData({
              file_type: 'purchase_others',
              pr_no: idx,
              pp_no: oIdx,
              file: { ...id, new_relation_addition: true }
            })
          )
          await savePurchaseRequest(idx)
        }
      })
    }
  }

  const DeleteOther = async (count, subCount, index, file) => {
    setOtherCardDeleting('other', count, index, true)
    try {
      let id = ''
      let file = purchase_requests[count].purchase_others[index][subCount]
      if (file?.file_obj?.id) {
        id = file?.file_obj?.id
      } else {
        id = file?.id
      }
      const purchaseRequestId = purchase_requests[count].attributes?.id
      const response = await handleDeleteOther(purchaseRequestId, id)
      if (!response) {
        throw new Error('Unable to delete other document')
      }
      dispatch(
        removePurchaseOtherFilterFiles({
          pr_no: count,
          pp_no: index,
          id
        })
      )
    } catch (error) {
      console.log(error)
      throw error
    } finally {
      setOtherCardDeleting('other', count, index, false)
    }
  }

  const DeleteOthers = async (idx, subCount, other) => {
    try {
      setOtherCardDeleting('other', idx, subCount, true)
      if (!other || other.length === 0) {
        dispatch(removePurchaseOthers({ pr_no: idx, pp_no: subCount }))
        return
      }

      // Delete files from server first
      const deleteOthers = other.map(async file => {
        let id = ''

        if (file?.file_obj?.id) {
          id = file?.file_obj?.id
        } else {
          id = file?.id
        }

        if (!id) return
        dispatch(
          setFilterFiles({
            file: {
              file_no: customer_files?.files.length,
              file: {},
              id: generateUUID(),
              fileName: file?.file_obj?.name || '',
              status: 'File uploaded successfully.',
              progress: 100,
              isUploaded: true,
              isError: false,
              file_obj: file?.file_obj || file
            }
          })
        )
        await handleDeleteOther(purchase_requests[idx].attributes.id, id)
      })
      await Promise.all(deleteOthers)

      // Remove the card from state only once after all files are processed
      dispatch(removePurchaseOthers({ pr_no: idx, pp_no: subCount }))
    } catch (error) {
      console.log(error)
    } finally {
      // Clean up the loading state to prevent memory leaks
      setLoadingStates(prev => {
        const newDeletingCards = { ...prev.deletingOtherCards }
        delete newDeletingCards[`other-${idx}-${subCount}`]
        return {
          ...prev,
          deletingOtherCards: newDeletingCards
        }
      })
    }
  }

  return (
    <PurchaseReceiptDocumentsContainer>
      {purchase_requests?.length > 0 &&
        purchase_requests?.map((pr, idx) => {
          // Find the selected affiliate data using both id and index for better matching
          const selectedAffiliate = selectedAffiliatesData.find(
            item => item.id === pr.attributes?.id || item.index === idx
          ) || {
            affiliate: pr.attributes?.affiliate_company_id || '',
            region: pr.attributes?.affiliate_region_id || '',
            location: pr.attributes?.affiliate_location_id || '',
            store: pr.attributes?.affiliate_store_id || '',
            storeLocator:
              pr.attributes?.affiliate_store_id &&
                pr.attributes?.affiliate_location_id
                ? `${pr.attributes.affiliate_store_id}/${pr.attributes.affiliate_location_id}`
                : ''
          }

          // Get filtered regions and locations dynamically
          const regions =
            selectedAffiliate.affiliate &&
              affiliatesData.regions[selectedAffiliate.affiliate]
              ? [
                { value: '', label: 'Select Region' },
                ...affiliatesData.regions[selectedAffiliate.affiliate].map(
                  region => ({
                    value: region.id,
                    label: region.name
                  })
                )
              ]
              : [{ value: '', label: 'Select Region' }]

          // Get store locator options for this purchase request
          const storeLocatorOptions =
            storeLocator.find(
              item =>
                item.id === selectedAffiliate.id ||
                item.id === (pr.attributes?.id || idx)
            )?.options || []
          const storeLocatorOptionsWithDefault = [
            { value: '', label: 'Select Store/Locator' },
            ...storeLocatorOptions
          ]

          return (
            <Box
              sx={{
                mb: 2,
                borderTop: '1px solid #F2DF97',
                borderRadius: '0.5rem',
                overflow: 'visible',
                position: 'relative',
                zIndex: 1
              }}
              key={pr.id || idx}
            >
              <DragAndDropCard
                text={`PURCHASE ${idx + 1}`.toUpperCase()}
                isDeletable={purchase_requests.length > 1}
                hasIconPurchase={true}
                onCardDelete={() => deletePurchaseRequest(idx)}
                label={`PURCHASE ${idx + 1}`.toUpperCase()}
                TextAdjustment="start"
                justifyContent="start"
                borderColor="white"
              >
                {/* DROPDOWNS and CARDS for this purchase */}
                <DropdownsRow container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <DropdownLabel>
                      Affiliate <RequiredField>*</RequiredField>
                    </DropdownLabel>
                    <StyledSelectWrapper>
                      <UiSelect
                        placeholder="Select Affiliate"
                        value={selectedAffiliate.affiliate}
                        onChange={async (value) => {
                          if (!value || value === '') {
                            return;
                          }
                          handleChangeAffiliate(
                            pr.attributes?.id ||
                            selectedAffiliate.id ||
                            `temp_${idx}_${Date.now()}`,
                            'affiliate',
                            value
                          )
                          await savePR(idx)
                        }}
                        options={affiliateOptions.filter(opt => opt.value !== '')}
                        name="affiliate"
                      />
                    </StyledSelectWrapper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DropdownLabel>Region</DropdownLabel>
                    <StyledSelectWrapper>
                      <UiSelect
                        placeholder="Select Region"
                        value={selectedAffiliate.region}
                        onChange={async (value) => {
                          if (!value || value === '') {
                            return;
                          }
                          handleChangeAffiliate(
                            pr.attributes?.id ||
                            selectedAffiliate.id ||
                            `temp_${idx}_${Date.now()}`,
                            'region',
                            value
                          )
                          await savePR(idx)
                        }}
                        options={regions.filter(opt => opt.value !== '')}
                        name="region"
                      />
                    </StyledSelectWrapper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DropdownLabel>Store/Locator</DropdownLabel>
                    <StyledSelectWrapper>
                      <UiSelect
                        placeholder="Select Store/Locator"
                        value={selectedAffiliate.storeLocator}
                        onChange={async (value) => {
                          if (!value || value === '') {
                            return;
                          }
                          handleChangeAffiliate(
                            pr.attributes?.id ||
                            selectedAffiliate.id ||
                            `temp_${idx}_${Date.now()}`,
                            'location',
                            value
                          )
                          await savePR(idx)
                        }}
                        options={storeLocatorOptionsWithDefault.filter(opt => opt.value !== '')}
                        name="storeLocator"
                      />
                    </StyledSelectWrapper>
                  </Grid>
                </DropdownsRow>
                <PurchaseReceiptDocumentsCardRow>
                  <PurchaseReceiptCard
                    text="POS Receipt"
                    files={pr.files?.pos_receipt || []}
                    onDrop={async item => {
                      // Handle POS receipt drop
                      await onDropPOS(idx, item)
                    }}
                    handleDeleteItem={DeletePOSItem}
                    count={idx}
                    deleteIcon={true}
                    haveOcr={
                      (pr?.attributes?.ocr?.invoice_date?.ocr ??
                        pr?.attributes?.ocr?.invoice_no?.ocr ??
                        0) === 1
                    }
                  />

                  {(() => {
                    const stampCards =
                      pr?.files?.purchase_stamp?.length > 0
                        ? pr.files.purchase_stamp
                        : [[]]
                    const validStampCardsWithIndices = stampCards
                      .map((card, originalIdx) => ({ originalIdx, card }))
                      .filter(({ card }) => card != null && Array.isArray(card));

                    return validStampCardsWithIndices.map(({ originalIdx, card: stampFiles }, displayIdx) => {
                      const displayText =
                        validStampCardsWithIndices.length <= 1 || displayIdx === 0
                          ? 'POS w. Stamp'
                          : `POS w. Stamp ${displayIdx + 1}`
                      return (
                        <Box key={`stamp-${idx}-${originalIdx}-${displayIdx}`}>
                          <PurchaseReceiptCard
                            text={displayText}
                            files={stampFiles || []}
                            count={idx}
                            deleteIcon={true}
                            haveOcr={0}
                            onDrop={async item => {
                              await onDropPOSStamp(idx, originalIdx, item)
                            }}
                            handleDeleteItem={(prIdx, fileIdx) => {
                              DeletePOSStampItem(prIdx, originalIdx, fileIdx)
                            }}
                            documentDeletewithfileLoader={
                              loadingStates.deletingOtherCards[`stamp-${idx}-${originalIdx}`] ||
                              false
                            }
                            icon={
                              validStampCardsWithIndices.length > 1 && originalIdx > 0 && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    DeletePOSStampCard(idx, originalIdx, stampFiles)
                                  }}
                                  sx={{
                                    color: 'error.main',
                                    '&:hover': {
                                      backgroundColor: 'error.light',
                                      color: 'white'
                                    }
                                  }}
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              )
                            }
                          />
                        </Box>
                      )
                    })
                  })()}

                  {/* Render existing payment methods */}
                  {(() => {
                    const visiblePaymentCount =
                      pr?.payment_methods?.filter(
                        pp => pp.attributes?.id && pp.attributes?.id !== ''
                      ).length || 0
                    let visibleIdx = -1
                    return pr?.payment_methods?.map((pp, pp_idx) => {
                      if (!(pp.attributes?.id && pp.attributes?.id !== '')) {
                        return null
                      }
                      visibleIdx += 1
                      const ccSlipLabel =
                        visiblePaymentCount <= 1 || visibleIdx === 0
                          ? 'Credit Card Slip'
                          : `Credit Card Slip ${visibleIdx + 1}`
                      return (
                        <PurchaseReceiptCard
                          key={pp.attributes?.id || pp_idx}
                          text={ccSlipLabel}
                          files={pp?.files?.slip || []}
                          haveOcr={
                            (pp?.attributes?.ocr?.total_amount?.ocr ??
                              pp?.attributes?.ocr?.amount?.ocr ??
                              0) === 1
                          }
                          onDrop={async item => {
                            await onDropCCSlip(idx, item, pp_idx)
                          }}
                          handleDeleteItem={(count, index, file) => {
                            DeleteCCSlip(count, index, pp_idx, file)
                          }}
                          documentDeletewithfileLoader={
                            loadingStates.deletingOtherCards[
                            `payment-${idx}-${pp_idx}`
                            ] || false
                          }
                          count={idx}
                          deleteIcon={true}
                          icon={
                            pr.payment_methods.length > 1 &&
                            pp_idx > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  deletePaymentMethod(
                                    idx,
                                    pp_idx,
                                    pp?.files?.slip
                                  )
                                }}
                                sx={{
                                  color: 'error.main',
                                  '&:hover': {
                                    backgroundColor: 'error.light',
                                    color: 'white'
                                  }
                                }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            )
                          }
                        />
                      )
                    })
                  })()}

                  <PurchaseReceiptCard
                    text="Factura (Invoice)"
                    files={pr.files?.sat_invoice || []}
                    onDrop={async item => {
                      await onDropFactura(item, idx)
                    }}
                    count={idx}
                    deleteIcon={true}
                    haveOcr={pr?.attributes?.ocr?.sat_invoice?.ocr === 1}
                    handleDeleteItem={(count, index) => {
                      DeleteFactura(count, index)
                    }}
                  />
                  {/* Render "Other" cards only when the user explicitly adds them via
                      the "+ Add Others" button. Don't show a blank default card. */}
                  {(() => {
                    const others = pr?.purchase_others || []
                    return others.map((other, oIdx) => {
                      const otherLabel =
                        others.length <= 1 || oIdx === 0
                          ? 'Other'
                          : `Other ${oIdx + 1}`
                      return (
                        <Box key={oIdx}>
                          <PurchaseReceiptCard
                            text={otherLabel}
                            files={other || []}
                            count={idx}
                            deleteIcon={true}
                            haveOcr={false}
                            onDrop={async id => {
                              await onDropOther(id, idx, oIdx)
                            }}
                            handleDeleteItem={(dIdx, subCount, file) => {
                              DeleteOther(dIdx, subCount, oIdx, file)
                            }}
                            documentDeletewithfileLoader={
                              loadingStates.deletingOtherCards[
                              `other-${idx}-${oIdx}`
                              ] || false
                            }
                            icon={
                              // Only show delete icon if there are actual purchase_others and it's not the first one
                              pr?.purchase_others?.length > 0 && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    DeleteOthers(idx, oIdx, other)
                                  }}
                                  sx={{
                                    color: 'error.main',
                                    '&:hover': {
                                      backgroundColor: 'error.light',
                                      color: 'white'
                                    }
                                  }}
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              )
                            }
                          />
                        </Box>
                      )
                    })
                  })()}
                </PurchaseReceiptDocumentsCardRow>

                {/* Individual purchase request action buttons */}
                <AddAnotherPurchaseRequestRow>
                  <AddButton
                    text="+ Add another card"
                    onClick={() => {
                      purchasePaymentVerify(
                        idx,
                        pr.payment_methods.length - 1,
                        'add'
                      )
                    }}
                    disabled={loadingStates.addPurchasePayment}
                    loading={loadingStates.addPurchasePayment}
                  />
                  <AddButton
                    text="+ Add POS w. Stamp"
                    onClick={() => {
                      // The first visible "POS w. Stamp" card is a default
                      // placeholder when redux has no stamps yet. To give the
                      // user a visible additional card on the first click,
                      // seed the placeholder slot too.
                      if (!pr?.files?.purchase_stamp?.length) {
                        dispatch(addPurchaseStampCard({ pr_no: idx }))
                      }
                      dispatch(addPurchaseStampCard({ pr_no: idx }))
                    }}
                  />
                  <AddButton
                    text="+ Add Others"
                    onClick={() => {
                      dispatch(addOtherPurchaseFile({ pr_no: idx }))
                    }}
                  />
                </AddAnotherPurchaseRequestRow>
              </DragAndDropCard>
            </Box>
          )
        })}
      <AddAnotherPurchaseRow>
        <AddButton
          text="+ Add another purchase"
          onClick={() =>
            purchaseSlipVerify(purchase_requests?.length - 1, 'add')
          }
          disabled={loadingStates.addPurchaseRequest}
          loading={loadingStates.addPurchaseRequest}
        />
        {/* <RunOcr /> */}
        <BatchOCR />
      </AddAnotherPurchaseRow>
    </PurchaseReceiptDocumentsContainer>
  )
}

const PurchaseReceiptDocumentsContainer = styled.div`
  padding: 20px 0 20px 0;
`

const PurchaseReceiptDocumentsCardRow = styled.div`
  margin: 15px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 1.3rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const AddButtonBox = styled.div`
  padding-left: 15px;
`

const AddAnotherPurchaseRow = styled.div`
  margin-top: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`

const AddAnotherPurchaseRequestRow = styled.div`
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`

const StyledSelectWrapper = styled.div`
  position: relative;
  z-index: 1000;

  .input-group.cig {
    margin-top: 0;
    margin-bottom: 0;
    position: relative;
    z-index: 1000;
  }

  .input-group.cig select.form-select {
    background-color: #fff !important;
    border: 1px solid #d0d0d0 !important;
    border-radius: 4px !important;
    padding: 8px 12px !important;
    padding-right: 32px !important;
    background-position: right 10px center !important;
    height: auto !important;
    cursor: pointer;
    min-height: 38px;
    font-size: 14px;
    color: #333;
    position: relative;
    z-index: 1000;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:focus {
      border-color: #d0d0d0 !important;
      outline: 0;
      box-shadow: none !important;
      z-index: 1001;
    }

    &:hover {
      border-color: #b0b0b0 !important;
    }

    option {
      padding: 8px;
    }
  }
`

const DropdownsRow = styled(Grid)`
  padding-top: 10px;
  padding-left: 10px;
  padding-right: 10px;
`

const DropdownLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #777777;
  margin-bottom: 8px;
  text-align: left;
`

export default PurchaseReceiptDocuments
